import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { paths } from '@/app/router/paths';
import {
    Button,
    Drawer,
    DrawerFooter,
    DrawerHeader,
    Field,
    Textarea,
    type ButtonSize,
} from '@/design-system';
import { InspectionReopenLockedError, inspectionApi } from '@/entities/inspection';
import { hasSurvey, orderKeys, sentMilestones, type Order } from '@/entities/order';
import { reportKeys } from '@/entities/report';
import { prepareSurvey } from '@/features/inspections/prepareSurvey';
import { useSessionStore } from '@/shared/api';
import { useOnlineStatus, useSyncState, useTenantDb } from '@/shared/offline';

import { reportCopy } from '../copy';
import { aimSurveyAt } from '../surveyJump';

/** Wat de weg terug naar de opname op dit moment is. */
type JumpKind =
    /** Geen opname met vragenlijst: er is niets om heen te gaan. */
    | 'none'
    /** De opname staat open; je kunt er zo heen. */
    | 'open'
    /** Ingediend, en deze persoon mag hem heropenen - ook na verzending. */
    | 'reopen'
    /** Ingediend, en heropenen is niet aan deze persoon. */
    | 'notAllowed'
    /** De keuring is geannuleerd; de opname gaat niet meer open. */
    | 'cancelled';

interface SurveyJumpValue {
    kind: JumpKind;
    /** Opent de weg terug naar dit boompad in de opname. */
    go: (sectionPath: string, what: string) => void;
}

const SurveyJumpContext = createContext<SurveyJumpValue | null>(null);

export interface SurveyJumpProviderProps {
    /** De keuring; `null` zolang ze laadt. */
    order: Order | null;
    children: ReactNode;
}

/**
 * De weg van het rapport terug naar de opname, één keer per scherm.
 *
 * Elke bevinding en elk onderdeel heeft een knop; die knoppen delen deze ene
 * plek, omdat er per scherm hoogstens één paneel open hoort te staan en omdat
 * heropenen maar één keer uitgelegd hoeft te worden.
 *
 * Wat er gebeurt hangt af van de stand van de keuring, en dat staat er hardop
 * bij: staat de opname open, dan ga je er meteen heen; is ze ingediend, dan
 * wordt ze eerst heropend (met een reden, die op de tijdlijn komt). Dat kan ook
 * als het rapport al verstuurd is: het paneel zegt dan dat het verstuurde rapport
 * blijft staan tot er een nieuwe versie is vastgesteld, en dat de klant die
 * daarna via dezelfde link ziet. Wie heropenen mag, zegt de API per opname.
 */
export function SurveyJumpProvider({ order, children }: SurveyJumpProviderProps) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const db = useTenantDb();
    const online = useOnlineStatus();
    const { flushNow } = useSyncState();
    const session = useSessionStore((state) => state.session);
    const userUuid = session?.user.uuid ?? 'anoniem';

    const service = order?.services.find(hasSurvey) ?? null;
    const inspectionUuid = service?.inspection.uuid ?? null;
    const submitted = service?.inspection.status === 'submitted';
    const reportWentOut = order !== null && sentMilestones(order).report;

    const kind: JumpKind =
        inspectionUuid === null
            ? 'none'
            : order?.status === 'cancelled'
              ? 'cancelled'
              : !submitted
                ? 'open'
                : service?.inspection.canReopen
                  ? 'reopen'
                  : 'notAllowed';

    const [target, setTarget] = useState<{ sectionPath: string; what: string } | null>(null);
    const [reason, setReason] = useState<string>(reportCopy.surveyJump.reopenReasonDefault);
    const [preparing, setPreparing] = useState(false);
    const [failed, setFailed] = useState(false);

    const close = useCallback(() => {
        setTarget(null);
        setFailed(false);
    }, []);

    /**
     * Haal de opname naar dit apparaat, zet haar klaar op het onderdeel en ga
     * erheen. Komt ze niet op tijd, dan gaan we toch: het opnamescherm zegt zelf
     * dat ze wordt klaargezet en opent vanzelf zodra ze er is.
     */
    const enter = useCallback(
        async (sectionPath: string, editable = false) => {
            if (!db || inspectionUuid === null) {
                setFailed(true);

                return;
            }

            setPreparing(true);

            try {
                await prepareSurvey({ db, inspectionUuid, sync: flushNow, editable });
                await aimSurveyAt(db, inspectionUuid, sectionPath, userUuid);
            } catch {
                /* Niets kunnen ophalen is geen reden om de weg terug te weigeren. */
            }

            setPreparing(false);
            close();
            void navigate(paths.survey(inspectionUuid));
        },
        [close, db, flushNow, inspectionUuid, navigate, userUuid],
    );

    const reopen = useMutation({
        mutationFn: (input: { inspectionUuid: string; reason: string; sectionPath: string }) =>
            inspectionApi.reopen(input.inspectionUuid, input.reason),
        onSuccess: (_result, input) => {
            if (order) {
                void queryClient.invalidateQueries({ queryKey: orderKeys.detail(order.uuid) });
                void queryClient.invalidateQueries({ queryKey: orderKeys.timeline(order.uuid) });
                void queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
                void queryClient.invalidateQueries({ queryKey: reportKeys.forOrder(order.uuid) });
            }

            /* Net heropend: hier kan nog de ingediende versie staan tot de synchronisatie de open brengt. */
            void enter(input.sectionPath, true);
        },
    });

    const value = useMemo<SurveyJumpValue>(
        () => ({
            kind,
            go: (sectionPath, what) => {
                setFailed(false);
                setReason(reportCopy.surveyJump.reopenReasonDefault);
                setTarget({ sectionPath, what });
            },
        }),
        [kind],
    );

    const copy = reportCopy.surveyJump;
    const busy = preparing || reopen.isPending;
    const problem = failed
        ? db
            ? copy.failed
            : copy.noEnvironment
        : reopen.error instanceof InspectionReopenLockedError
          ? copy.cancelledBody
          : reopen.error
            ? copy.failed
            : null;

    return (
        <SurveyJumpContext.Provider value={value}>
            {children}

            <Drawer
                open={target !== null}
                onClose={close}
                side="right"
                width={420}
                title={titleFor(kind)}
            >
                {target ? (
                    <>
                        <DrawerHeader
                            icon="survey"
                            eyebrow={target.what}
                            title={titleFor(kind)}
                            description={bodyFor(kind, reportWentOut)}
                            onClose={close}
                            closeLabel={copy.close}
                        />

                        {kind === 'reopen' ? (
                            <div className="flex flex-col gap-3 p-5">
                                <Field label={copy.reopenReason}>
                                    <Textarea
                                        rows={3}
                                        maxLength={500}
                                        placeholder={copy.reopenReasonPlaceholder}
                                        value={reason}
                                        onChange={(event) => setReason(event.target.value)}
                                    />
                                </Field>
                                {online ? null : (
                                    <p
                                        role="status"
                                        className="m-0 text-[13px] leading-[1.5] text-[var(--warn-text)]"
                                    >
                                        {copy.reopenOffline}
                                    </p>
                                )}
                            </div>
                        ) : null}

                        {problem ? (
                            <p
                                role="alert"
                                className="m-0 px-5 pb-3 text-[13px] leading-[1.5] text-[var(--danger)]"
                            >
                                {problem}
                            </p>
                        ) : null}

                        <DrawerFooter>
                            {kind === 'open' ? (
                                <Button
                                    variant="primary"
                                    size="md"
                                    disabled={busy}
                                    onClick={() => void enter(target.sectionPath)}
                                >
                                    {preparing ? copy.busy : copy.goConfirm}
                                </Button>
                            ) : null}

                            {kind === 'reopen' && inspectionUuid !== null ? (
                                <Button
                                    variant="primary"
                                    size="md"
                                    disabled={busy || !online || reason.trim().length < 3}
                                    onClick={() =>
                                        reopen.mutate({
                                            inspectionUuid,
                                            reason: reason.trim(),
                                            /* Het doel reist mee, zodat `onSuccess` weet waarheen. */
                                            sectionPath: target.sectionPath,
                                        })
                                    }
                                >
                                    {busy
                                        ? preparing
                                            ? copy.busy
                                            : copy.reopenBusy
                                        : copy.reopenConfirm}
                                </Button>
                            ) : null}

                            <Button variant="outline" size="md" disabled={busy} onClick={close}>
                                {kind === 'open' || kind === 'reopen'
                                    ? copy.reopenCancel
                                    : copy.close}
                            </Button>
                        </DrawerFooter>
                    </>
                ) : null}
            </Drawer>
        </SurveyJumpContext.Provider>
    );
}

export interface SurveyJumpButtonProps {
    /** Het boompad van het onderdeel of de bevinding; zonder pad geen knop. */
    sectionPath: string | null;
    /** Waar het over gaat: "35 Kozijnen", "Houtrot onderdorpel". */
    what: string;
    /** `short` is de smalle variant voor een tabelcel. */
    variant?: 'full' | 'short';
    size?: ButtonSize;
}

/**
 * "Aanpassen in de opname" bij één bevinding of één hoofdstuk.
 *
 * De knop staat er ook als de opname is ingediend of het rapport verstuurd is:
 * het paneel zegt dan wat er gebeurt als je hem heropent, of wie dat wel mag.
 */
export function SurveyJumpButton({
    sectionPath,
    what,
    variant = 'full',
    size = 'xs',
}: SurveyJumpButtonProps) {
    const jump = useContext(SurveyJumpContext);

    if (!jump || jump.kind === 'none' || sectionPath === null) {
        return null;
    }

    const copy = reportCopy.surveyJump;

    return (
        <Button
            variant="outline"
            size={size}
            aria-label={copy.actionLabel(what)}
            onClick={() => jump.go(sectionPath, what)}
        >
            {variant === 'short' ? copy.short : copy.action}
        </Button>
    );
}

function titleFor(kind: JumpKind): string {
    const copy = reportCopy.surveyJump;

    return kind === 'reopen'
        ? copy.reopenTitle
        : kind === 'cancelled'
          ? copy.cancelledTitle
          : kind === 'notAllowed'
            ? copy.notAllowedTitle
            : copy.goTitle;
}

/** Na verzending zegt heropenen ook wat er met het verstuurde rapport gebeurt. */
function bodyFor(kind: JumpKind, reportWentOut: boolean): string {
    const copy = reportCopy.surveyJump;

    return kind === 'reopen'
        ? reportWentOut
            ? copy.reopenBodySent
            : copy.reopenBody
        : kind === 'cancelled'
          ? copy.cancelledBody
          : kind === 'notAllowed'
            ? copy.notAllowedBody
            : copy.goBody;
}
